import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import { formatPatientProfileValue } from "../../../patientProfileModal.utils";
import type {
  EmergencyContact,
  PreparednessDraft,
  PreparednessField,
} from "./emergencyPreparedness.types";

export const priorityCodeOptions = [1, 2, 3] as const;
export const evacuationZoneOptions = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "No Zone",
  "Not availalable",
  "Other",
];
export const evacuationLocationOptions = [
  "Church",
  "Family",
  "Friend",
  "Hospital",
  "Nursing Home",
  "Adult Home",
  "Shelter",
  "Other",
];
export const electricEquipmentDependencyOptions = [
  "Grab Bars",
  "Shower Chair",
  "Bedpan",
  "Chux",
  "Hand Rails",
  "NG Tube",
  "Sterile Gloves",
  "Wound Care Supplies",
  "Bedside Commode",
  "Crutches",
  "Hospital Bed",
  "Non Sterile Gloves",
  "Trapeze",
  "Cane",
  "Diapers",
  "Injection Supplies",
  "Ostomy Supplies",
  "Urinalysis Supplies",
  "Catheter",
  "Enema",
  "Irrigation Set",
  "Prosthesis",
  "Venipuncture Supplies",
  "Central Line Supplies",
  "Gastrostomy Tube",
  "IV Supplies",
  "Shower Rails",
  "Walker",
  "Chair Cushion",
  "Glucometer / Strips",
  "Low Air Loss Mattress",
  "Side Rails",
  "Wheelchair",
];
export const mobilityStatusOptions = [
  "TAL 1: Non-ambulatory Bariatric",
  "TAL 1: Non-ambulatory Stretcher",
  "TAL 1: Non-ambulatory Vent",
  "TAL 1: Wheelchair-IV saline lock/discontinued",
  "TAL 2: Ambulatory with assist",
  "TAL 2: Chair-bound/Wheelchair",
  "TAL 2: Wheelchair IV infusion Cntinious",
  "TAL 3: Ambulatory Independent",
];

export const preparednessFields = [
  { field: "priorityCode", label: "Priority Code" },
  { field: "evacuationZone", label: "Evacuation Zone" },
  { field: "evacuationLocation", label: "Evacuation Location" },
  { field: "mobilityStatus", label: "Mobility Status" },
  { field: "electricEquipmentDependency", label: "Equipment Dependency" },
] satisfies Array<{ field: PreparednessField; label: string }>;

export function createEmptyEmergencyContact(isOnboardingAgency: boolean): EmergencyContact {
  return {
    firstName: "",
    lastName: "",
    relationship: "",
    // The onboarding agency is not asked "lives with patient"; patient_contact still needs a value.
    livesWithPatient: isOnboardingAgency
      ? false
      : (undefined as unknown as EmergencyContact["livesWithPatient"]),
    emergencyPhoneNumber1: "",
    emergencyPhoneNumber2: "",
    address: "",
    ...(isOnboardingAgency ? { email: "" } : {}),
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isInvalidEmergencyContactEmail(email: string | null | undefined) {
  return (
    email !== null &&
    email !== undefined &&
    email.trim() !== "" &&
    !EMAIL_PATTERN.test(email.trim())
  );
}

export function normalizeEmergencyContact(contact: EmergencyContact): EmergencyContact {
  const phoneNumber2 = contact.emergencyPhoneNumber2.trim();

  return {
    ...contact,
    address: contact.address.trim(),
    emergencyPhoneNumber1: phoneFormatter.formatE164(contact.emergencyPhoneNumber1),
    emergencyPhoneNumber2:
      phoneNumber2 === "" ? "" : phoneFormatter.formatE164(contact.emergencyPhoneNumber2),
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    relationship: contact.relationship.trim(),
    ...(contact.email !== undefined ? { email: contact.email?.trim() || null } : {}),
  };
}

export function getEmergencyContactsValidationMessage(contacts: EmergencyContact[]) {
  const invalidContactIndex = contacts.findIndex(
    (contact) =>
      contact.firstName.trim() === "" ||
      contact.lastName.trim() === "" ||
      contact.livesWithPatient === undefined ||
      contact.emergencyPhoneNumber1.trim() === "" ||
      !phoneFormatter.isValidNumber(contact.emergencyPhoneNumber1) ||
      (contact.emergencyPhoneNumber2.trim() !== "" &&
        !phoneFormatter.isValidNumber(contact.emergencyPhoneNumber2)) ||
      isInvalidEmergencyContactEmail(contact.email)
  );

  if (invalidContactIndex === -1) {
    return null;
  }

  return `Emergency Contact #${invalidContactIndex + 1} has missing or invalid required fields.`;
}

export function createPreparednessDraft(patient: PatientProfilePatient): PreparednessDraft {
  return {
    electricEquipmentDependency: patient.electricEquipmentDependency,
    evacuationLocation: patient.evacuationLocation,
    evacuationZone: patient.evacuationZone,
    mobilityStatus: patient.mobilityStatus,
    priorityCode: patient.priorityCode,
  };
}

export function getPreparednessDisplayValue(
  field: PreparednessField,
  draft: PreparednessDraft,
  patient: PatientProfilePatient
) {
  if (field === "priorityCode") {
    return formatPatientProfileValue(draft.priorityCode ?? patient.priorityCode);
  }

  if (field === "evacuationZone") {
    return formatPatientProfileValue(draft.evacuationZone ?? patient.evacuationZone);
  }

  if (field === "evacuationLocation") {
    return formatPatientProfileValue(draft.evacuationLocation ?? patient.evacuationLocation);
  }

  if (field === "mobilityStatus") {
    return formatPatientProfileValue(draft.mobilityStatus ?? patient.mobilityStatus);
  }

  return formatPatientProfileValue(
    draft.electricEquipmentDependency ?? patient.electricEquipmentDependency
  );
}

export function splitElectricEquipmentDependency(value: string | null | undefined) {
  if (value === null || value === undefined || value.trim() === "") {
    return [];
  }

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getElectricEquipmentDependencyOptions(value: string | null | undefined) {
  const customValues = splitElectricEquipmentDependency(value).filter(
    (item) => !electricEquipmentDependencyOptions.includes(item)
  );

  return [...electricEquipmentDependencyOptions, ...customValues];
}
